import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Chip,
  Fade,
} from '@mui/material';
import {
  ArrowForward,
  Schedule,
  TrendingUp,
  Speed,
  Support,
  CheckCircle,
  CloudSync,
} from '@mui/icons-material';
import { useDemoModal } from '../hooks/useDemoModal';
import MagneticButton from './MagneticButton';
import { useThemeMode } from '../contexts/ThemeContext';
import { CountUp } from './CountUp';

function CallToAction() {
  const { openModal } = useDemoModal();
  const { isLight } = useThemeMode();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: 85, suffix: '%', label: 'Time Saved' },
    { value: 300, suffix: '%', label: 'ROI' },
    { value: 99.9, suffix: '%', decimals: 1, label: 'Uptime' },
    { value: null, display: '24/7', label: 'Support' },
  ];

  const benefits = [
    'No-Code Designer',
    'Enterprise Security',
    'Real-time Analytics',
    'Pre-built Connectors',
    'Cloud & On-Prem',
    'Dedicated Manager',
  ];

  return (
    <Box
      ref={sectionRef}
      component="section"
      sx={{
        py: { xs: 6, md: 10 },
        position: 'relative',
        overflow: 'hidden',
        background: isLight
          ? 'transparent'
          : 'linear-gradient(145deg, #020617 0%, #0f172a 30%, #1e293b 60%, #334155 70%, #0f172a 90%, #020617 100%)',
        backdropFilter: isLight ? 'blur(20px)' : undefined,
        WebkitBackdropFilter: isLight ? 'blur(20px)' : undefined,
      }}
    >
      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
        <Fade in={isVisible} timeout={800}>
          <Box sx={{ 
            textAlign: 'center',
            ...(isLight && {
              background: 'rgba(255, 255, 255, 0.5)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid rgba(255, 255, 255, 0.7)',
              borderRadius: 5,
              p: { xs: 4, md: 6 },
              boxShadow: '0 16px 48px rgba(255, 255, 255, 0.6), 0 1px 3px rgba(0,0,0,0.04)',
            }),
          }}>
            {/* Heading */}
            <Typography
              variant="h2"
              component="h2"
              sx={{
                fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3.5rem' },
                fontWeight: 900,
                lineHeight: 1.1,
                mb: 2,
                color: isLight ? '#0f172a' : '#ffffff',
              }}
            >
              Ready to{' '}
              <Box
                component="span"
                sx={{
                  background: isLight
                    ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                    : 'linear-gradient(135deg, #38bdf8, #8b5cf6)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Automate
              </Box>
              ?
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { xs: '1rem', md: '1.15rem' },
                color: isLight
                  ? 'rgba(15,23,42,0.7)'
                  : 'rgba(248,250,252,0.7)',
                maxWidth: 500,
                mx: 'auto',
                mb: { xs: 4, md: 5 },
              }}
            >
              Enterprise-proven iPaaS and middleware, now available as a fully managed
              cloud platform. Start with a free pilot or talk to our team.
            </Typography>

            {/* Stats row */}
            <Stack
              direction="row"
              spacing={{ xs: 2, sm: 4 }}
              justifyContent="center"
              sx={{ mb: { xs: 4, md: 5 } }}
            >
              {stats.map((stat, i) => (
                <Box key={i} sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 900,
                      fontSize: { xs: '1.5rem', sm: '2rem' },
                      color: isLight ? '#6366f1' : '#10b981',
                      lineHeight: 1,
                    }}
                  >
                    {stat.value !== null ? (
                      <CountUp
                        end={stat.value}
                        trigger={isVisible}
                        suffix={stat.suffix || ''}
                        decimals={stat.decimals || 0}
                        duration={1800}
                      />
                    ) : (
                      stat.display
                    )}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isLight
                        ? 'rgba(15,23,42,0.5)'
                        : 'rgba(248,250,252,0.5)',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {/* Benefits */}
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1}
              justifyContent="center"
              sx={{ mb: { xs: 4, md: 5 } }}
            >
              {benefits.map((b, i) => (
                <Chip
                  key={i}
                  icon={<CheckCircle sx={{ fontSize: '0.9rem' }} />}
                  label={b}
                  size="small"
                  sx={{
                    background: isLight
                      ? 'rgba(255, 255, 255, 0.6)'
                      : 'rgba(16, 185, 129, 0.15)',
                    color: isLight ? '#4f46e5' : '#10b981',
                    border: isLight
                      ? '1px solid rgba(255, 255, 255, 0.8)'
                      : '1px solid rgba(16, 185, 129, 0.3)',
                    backdropFilter: isLight ? 'blur(12px)' : undefined,
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    '& .MuiChip-icon': {
                      color: isLight ? '#6366f1' : '#34d399',
                    },
                    transition: 'all 0.25s ease',
                    cursor: 'default',
                    '&:hover': {
                      transform: 'translateY(-2px) scale(1.05)',
                      boxShadow: isLight
                        ? '0 4px 12px rgba(99, 102, 241, 0.15)'
                        : '0 4px 12px rgba(16, 185, 129, 0.2)',
                    },
                  }}
                />
              ))}
            </Stack>

            {/* Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ mb: 3 }}
            >
              <MagneticButton>
              <Button
                variant="contained"
                size="large"
                onClick={openModal}
                endIcon={<ArrowForward />}
                sx={{
                  py: 1.5,
                  px: 5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  borderRadius: 3,
                  background: isLight
                    ? 'linear-gradient(135deg, #6366f1, #4f46e5)'
                    : 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 50%, #8b5cf6 100%)',
                  color: '#ffffff',
                  boxShadow: isLight
                    ? '0 8px 32px rgba(99, 102, 241, 0.3)'
                    : '0 8px 32px rgba(56, 189, 248, 0.35)',
                  transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-75%',
                    width: '50%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.25), transparent)',
                    transform: 'skewX(-20deg)',
                    transition: 'none',
                  },
                  '&:hover': {
                    transform: 'translateY(-3px) scale(1.02)',
                    background: isLight
                      ? 'linear-gradient(135deg, #4f46e5, #4338ca)'
                      : 'linear-gradient(135deg, #22d3ee 0%, #38bdf8 50%, #a78bfa 100%)',
                    boxShadow: isLight
                      ? '0 12px 40px rgba(99, 102, 241, 0.4)'
                      : '0 12px 40px rgba(56, 189, 248, 0.45)',
                    '&::before': {
                      animation: 'shine-sweep 0.6s ease forwards',
                    },
                  },
                  '&:active': {
                    transform: 'translateY(-1px) scale(0.97)',
                    transition: 'transform 0.1s ease',
                  },
                }}
              >
                Request A Demo
              </Button>
              </MagneticButton>

              <MagneticButton>
              <Button
                variant="outlined"
                size="large"
                onClick={() => window.open('https://studio.alphanetix.io', '_blank')}
                endIcon={<CloudSync />}
                sx={{
                  py: 1.5,
                  px: 4,
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  borderRadius: 3,
                  borderColor: isLight
                    ? 'rgba(99, 102, 241, 0.3)'
                    : 'rgba(16, 185, 129, 0.4)',
                  color: isLight ? '#6366f1' : '#10b981',
                  transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&:hover': {
                    borderColor: isLight ? '#6366f1' : '#10b981',
                    background: isLight
                      ? 'rgba(99, 102, 241, 0.05)'
                      : 'rgba(16, 185, 129, 0.1)',
                    transform: 'translateY(-2px)',
                  },
                  '&:active': {
                    transform: 'translateY(0px) scale(0.97)',
                    transition: 'transform 0.1s ease',
                  },
                }}
              >
                Try Cloud Edition
              </Button>
              </MagneticButton>
            </Stack>

            {/* Trust */}
            <Typography
              variant="caption"
              sx={{
                color: isLight
                  ? 'rgba(15,23,42,0.45)'
                  : 'rgba(248,250,252,0.45)',
                fontSize: '0.8rem',
              }}
            >
              ✓ Free pilot available&nbsp;&nbsp;✓ Cloud & On-Premise&nbsp;&nbsp;✓ Enterprise support
            </Typography>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
}

export default CallToAction;
